// MaintenanceSelect.js
import React, { useMemo } from 'react';
import { FILTER_CONFIG, UI_TYPES } from './FilterConfig';
import { EntityPickerField } from '../../../common/selectComponents/EntityPicker/EntityPickerField';
import { EntityPickerSelect } from '../../../common/selectComponents/EntityPicker/EntityPickerSelect';
// ADDED: Imports for new filter types
import { DateInput } from '../../../common/Input';
import { Select } from '../../../common/Select';

/**
 * MaintenanceSelect - Factory Component
 * Renders the appropriate filter UI based on the FilterConfig.
 */
export const MaintenanceSelect = ({ type, value, onChange, activeFilterState, allowAll }) => {
  const config = FILTER_CONFIG[type];

  // If no config found for this key, render nothing

  // 1. Logic for Maintenance/Entity Pickers (Legacy Support)
  const displayField = config.pickerConfig?.displayFields?.primary || "description";
  const displayValue = value ? value[displayField] : "";

  // 2. Dependency Logic (e.g., PayrollPeriod depends on PayType)
  const dependencyKey = config?.dependsOn?.[0];
  const dependencyValue = dependencyKey
    ? activeFilterState?.[dependencyKey]?.id
    : null;

  const isPayrollPeriod = config?.label === "PayrollPeriod";
  const isPositionByDepartment = config?.label === "Position";

  const disableFilter = 
    (isPayrollPeriod && config?.dependsOn && !dependencyValue) ||
    (isPositionByDepartment && config?.dependsOn && !dependencyValue);

  // 3. Memoize Config for Pickers that have dynamic API endpoints (Dependencies)
  const updatedConfig = useMemo(() => {
    if (isPayrollPeriod && config?.dependsOn && dependencyValue) {
      console.log("isPayrollPeriod");
      return {
        ...config,
        pickerConfig: {
          ...config.pickerConfig,
          apiEndpoint: `/api/PayrollPeriod/active/paytype/${dependencyValue}`
        }
      };
    }

    if (isPositionByDepartment && config?.dependsOn && dependencyValue) {
      console.log("isPositionByDepartment");
      return {
        ...config,
        pickerConfig: {
          ...config.pickerConfig,
          apiEndpoint: `/api/Position/active/department/${dependencyValue}`
        }
      };
    }
    
    return config;

  }, [config, dependencyValue, isPayrollPeriod]);

  if (!config) return null;

  // 4. Render Engine based on UI_TYPE
  const renderFilterControl = () => {
    switch (config.uiType) {
      case UI_TYPES.PICKER:
        return (
          <EntityPickerField
            config={updatedConfig.pickerConfig}
            entityId={value?.id}
            displayValue={displayValue}
            onSelect={(item) => onChange(item)}
            onClear={() => onChange(null)}
            placeholder={allowAll ? "All" : "--Selecte Data--"}
            isRequired={false}
            allowClear={true}
            disabled={disableFilter}
          />
        );

      case UI_TYPES.DROPDOWN:
        return (
          <EntityPickerSelect
            config={updatedConfig.pickerConfig}
            value={value}
            onChange={(item) => onChange(item)}
            placeholder={allowAll === true ? "All" : "--Select Data--"}
            className="h-[34px] text-sm border-gray-300 rounded-md shadow-sm"
            disabled={disableFilter}
            allowAll={allowAll}
          />
        );

      case UI_TYPES.DATE: // ADDED: Support for Date Input
        return (
          <DateInput
            value={value || ""} // value is a raw string "YYYY-MM-DD"
            onChange={(e) => onChange(e.target.value)}
            className="h-[34px] !py-1"
            isDisabled={disableFilter}
          />
        );

      case UI_TYPES.SELECT_STATIC: // ADDED: Support for Static Dropdowns (not from DB)
        return (
          <Select
            options={config.options || []}
            value={value?.id || value || ""} // Handles both object and primitive state
            onChange={(e) => onChange(e.target.value)}
            placeholder="-- All --"
            className="h-[34px] text-sm"
            disabled={disableFilter}
            allowSelectAll={allowAll}
          />
        );

      case UI_TYPES.TEXT: // ADDED: Support for raw text search
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Search..."
            className="w-full border border-gray-300 rounded-md px-3 h-[34px] text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
            disabled={disableFilter}
          />
        );

      default:
        return <div className="text-xs text-red-500">Invalid UI Type</div>;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {config.label}
      </label>

      <div className="w-full">
        {renderFilterControl()}
      </div>
    </div>
  );
};